// Hand-rolled replacement for `vue-howler` (KAT-943 Phase 2b): `vue-howler` has been abandoned
// since 2017 and never shipped a Vue 3 build. This is a faithful re-implementation of its
// Options-API mixin (same props/data/computed/methods/events), so `AudioPlayer.vue` and
// `MenuAudioPlayer.vue` didn't need to change at all beyond swapping the import. Ported from
// https://registry.npmjs.org/vue-howler/-/vue-howler-0.7.0.tgz, with `beforeDestroy` renamed to
// `beforeUnmount` for Vue 3.
import { Howl } from 'howler'

function mathClamp (x, min, max) {
  return x < min ? min : x > max ? max : x
}

export default {
  // Declares every event this mixin can emit, matching the Howl events it binds in
  // `_initialize()` — see MenuItem.vue's comment for why this matters in Vue 3.
  emits: ['load', 'loaderror', 'playerror', 'play', 'end', 'pause', 'stop', 'mute', 'volume', 'rate', 'seek', 'fade'],

  props: {
    // An array of audio file urls
    sources: {
      type: Array,
      required: true,
      validator: (sources) => sources.every((source) => typeof source === 'string' && source.length > 0)
    },
    // Whether to start the playback when the component is mounted
    autoplay: {
      type: Boolean,
      default: false
    },
    // Whether to start the playback again automatically after it is done playing
    loop: {
      type: Boolean,
      default: false
    },
    // Whether to start downloading the audio file when the component is mounted
    preload: {
      type: Boolean,
      default: true
    },
    // Whether to force HTML5 Audio
    html5: {
      type: Boolean,
      default: false
    },
    // An array of audio file types
    formats: {
      type: Array,
      default: () => []
    },
    // Whether to enable the withCredentials flag on XHR requests used to fetch audio files
    // when using Web Audio API
    xhrWithCredentials: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      // The Howl instance used for playback
      _howl: null,
      // Whether audio is currently playing
      playing: false,
      // Whether the audio playback is muted
      muted: false,
      // The volume of the playback on a scale of 0 to 1
      volume: 1.0,
      // The rate (speed) of the playback on a scale of 0.5 to 4
      rate: 1.0,
      // The position of playback in seconds
      seek: 0,
      // The duration of the audio in seconds
      duration: 0,
      // Functions that poll the Howl instance to update various data
      _polls: {
        seek: {
          id: null,
          interval: 1000 / 4, // 4 times per second (4Hz)
          hook: () => { this.seek = this._howl.seek() }
        }
      },
      // A list of howl events to listen to and functions to call when they are triggered
      _howlEvents: [
        { name: 'load', hook: () => { this.duration = this._howl.duration() } },
        'loaderror',
        'playerror',
        { name: 'play', hook: () => { this.playing = true } },
        { name: 'end', hook: () => { this.playing = false } },
        { name: 'pause', hook: () => { this.playing = false } },
        {
          name: 'stop',
          hook: () => {
            this.playing = false
            if (this._howl != null) this.seek = this._howl.seek()
          }
        },
        'mute',
        { name: 'volume', hook: () => { this.volume = this._howl.volume() } },
        { name: 'rate', hook: () => { this.rate = this._howl.rate() } },
        { name: 'seek', hook: () => { this.seek = this._howl.seek() } },
        'fade'
      ]
    }
  },

  computed: {
    // The progress of the playback on a scale of 0 to 1
    progress () {
      if (this.duration === 0) return 0
      return this.seek / this.duration
    }
  },

  created () {
    this._initialize()
  },

  beforeUnmount () {
    this._cleanup()
  },

  watch: {
    playing (playing) {
      // Update the seek
      this.seek = this._howl.seek()

      if (playing) {
        // Start the seek poll
        this._polls.seek.id = setInterval(this._polls.seek.hook, this._polls.seek.interval)
      } else {
        // Stop the seek poll
        clearInterval(this._polls.seek.id)
      }
    },
    sources () {
      this._reinitialize()
    }
  },

  methods: {
    // Reinitialize the Howler player
    _reinitialize () {
      this._cleanup(false)
      this._initialize()
    },

    // Initialize the Howler player
    _initialize () {
      this._howl = new Howl({
        src: this.sources,
        volume: this.volume,
        html5: this.html5,
        loop: this.loop,
        preload: this.preload,
        autoplay: this.autoplay,
        mute: this.muted,
        rate: this.rate,
        format: this.formats,
        xhrWithCredentials: this.xhrWithCredentials
      })

      const duration = this._howl.duration()
      this.duration = duration

      if (duration > 0) {
        // The audio file(s) have been cached. Howler won't emit a load event, so we do this
        // manually.
        this.$emit('load')
      }

      // Bind to all Howl events
      this._howlEvents = this._howlEvents.map((event) => {
        // Normalize string shorthands to objects
        if (typeof event === 'string') event = { name: event }

        // Create and bind a handler
        const handler = (id, details) => {
          if (typeof event.hook === 'function') event.hook(id, details)
          this.$emit(event.name, id, details)
        }
        this._howl.on(event.name, handler)

        // Keep the handler around so it can be unbound later
        return { ...event, handler }
      })
    },

    // Clean up the Howler player
    _cleanup (resetSettings = true) {
      // Stop all playback
      if (this._howl) this.stop()

      // Stop all polls
      Object.values(this._polls).forEach((poll) => {
        if (poll.id != null) clearInterval(poll.id)
      })

      // Clear all event listeners
      this._howlEvents = this._howlEvents.map((event) => {
        if (event.handler) {
          if (this._howl) this._howl.off(event.name, event.handler)
          const { handler, ...rest } = event
          return rest
        }
        return event
      })

      // Destroy the Howl instance
      this._howl = null
      this.duration = 0

      if (resetSettings) {
        this.muted = false
        this.volume = 1.0
        this.rate = 1.0
      }
    },

    // Start the playback
    play () {
      if (!this.playing) this._howl.play()
    },

    // Pause the playback
    pause () {
      if (this.playing) this._howl.pause()
    },

    // Toggle playing or pausing the playback
    togglePlayback () {
      if (!this.playing) {
        this._howl.play()
      } else {
        this._howl.pause()
      }
    },

    // Stop the playback (also resets the seek to 0)
    stop () {
      this._howl.stop()
    },

    // Mute the playback
    mute () {
      this._howl.mute(true)
      this.muted = true
    },

    // Unmute the playback
    unmute () {
      this._howl.mute(false)
      this.muted = false
    },

    // Toggle muting and unmuting the playback
    toggleMute () {
      this._howl.mute(!this.muted)
      this.muted = !this.muted
    },

    // Set the volume of the playback (clamped between 0 and 1)
    setVolume (volume) {
      if (typeof volume !== 'number') throw new Error(`volume must be a number, got a ${typeof volume} instead`)
      this._howl.volume(mathClamp(volume, 0, 1))
    },

    // Set the rate (speed) of the playback (clamped between 0.5 and 4)
    setRate (rate) {
      if (typeof rate !== 'number') throw new Error(`rate must be a number, got a ${typeof rate} instead`)
      this._howl.rate(mathClamp(rate, 0.5, 4))
    },

    // Set the position of the playback in seconds (clamped between 0 and the duration)
    setSeek (seek) {
      if (typeof seek !== 'number') throw new Error(`seek must be a number, got a ${typeof seek} instead`)
      this._howl.seek(mathClamp(seek, 0, this.duration))
    },

    // Set the progress of the playback (clamped between 0 and 1)
    setProgress (progress) {
      if (typeof progress !== 'number') throw new Error(`progress must be a number, got a ${typeof progress} instead`)
      this.setSeek(mathClamp(progress, 0, 1) * this.duration)
    }
  }
}
